import Slider from "react-slick";
import { useDispatch } from "react-redux";
import { Box, Button, Fade, Hidden, Modal, makeStyles } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import DoneIcon from "@material-ui/icons/Done";
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";

import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { quateAddonQuantityValue } from "../../../../app/utils";
import { QuoteLineItem } from "../../../../api/Quote/QuoteLineItem";
import { AddonForm } from "../../../../api/Addon/AddonApi";
import { changeQuantityAddonQuote } from "../../../../store/QuoteSlice";
import { fontFamily } from "../../../../app/theme";
import { PriceFormat } from "../../../../components/typography/PriceFormat";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: "4px",
  boxShadow: 24,
  p: 8
};

export default function AddonModal({
  open,
  data,
  quoteId,
  handleClose,
  onAddAddon,
  viewOnly
}: {
  open: boolean;
  data?: QuoteLineItem;
  quoteId: string;
  handleClose: () => void;
  onAddAddon: (addon: QuoteLineItem) => void;
  viewOnly?: boolean;
}) {
  const classes = useAddOnStyles();
  const dispatch = useDispatch();
  const { isMoreThanSm } = useBreakpoint();
  const settings = {
    dots: true,
    fade: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    classes: classes.slider
  };
  const onChangeQuantity = (addon: QuoteLineItem, quantity: number) => {
    dispatch(
      changeQuantityAddonQuote(quoteId, {
        addonId: addon.productId,
        quantity: quantity
      } as AddonForm)
    );
  };
  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Box
          sx={style}
          flexDirection={isMoreThanSm ? "row" : "column"}
          alignItems={isMoreThanSm ? "start" : "center"}
          className={classes.container}
        >
          <Box position={"absolute"} top={isMoreThanSm ? "10px" : "10px"} right={isMoreThanSm ? "10px" : "5px"}>
            <Button onClick={handleClose}>
              <ClearIcon />
            </Button>
          </Box>
          <Hidden smUp>
            <Box className={classes.title}>{data?.name}</Box>
          </Hidden>
          <Box width={156} height={217} mt={1} ml={1}>
            <Slider {...settings}>
              <Box>
                <img alt={data?.name} src={data?.imageUrl} width={156} height={217} className={classes.img} />
              </Box>
            </Slider>
          </Box>
          <Box ml={isMoreThanSm ? 10 : 0}>
            <Hidden smDown>
              <Box className={classes.title}>{data?.name}</Box>
            </Hidden>
            <Box className={classes.subTitle}> {data?.highLevelDescription}</Box>
            <Box className={classes.price}>{data && <PriceFormat value={quateAddonQuantityValue(data)} />}</Box>
            <Box display="flex" mb={4}>
              <Box className={classes.showQuantity}>
                <button
                  onClick={() => {
                    if (data) onChangeQuantity(data, data.quantity - 1);
                  }}
                  disabled={data?.quantity === 1 || viewOnly}
                  className={classes.button}
                >
                  <RemoveIcon />
                </button>
                <span>{data?.quantity}</span>
                <button
                  className={classes.button}
                  disabled={data?.quantity === data?.maxQuantity || viewOnly}
                  onClick={() => {
                    if (data) onChangeQuantity(data, data.quantity + 1);
                  }}
                >
                  <AddIcon />
                </button>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                className={classes.btnAdd}
                disabled={viewOnly}
                onClick={() => {
                  if (data) onAddAddon(data);
                  handleClose();
                }}
              >
                <Box pr={data?.selected ? 1 : 0} component="span" alignItems="center">
                  {data?.selected && <DoneIcon className={classes.icon} fontSize="small" />}
                </Box>
                {data?.selected ? "Added" : "Add"}
              </Button>
            </Box>
            <Box mb={2}>Highlights</Box>
            <Box>{data?.description}</Box>
            <Box ml={-3}>
              <ul>
                {data?.highlights?.map((highlight, idx) => (
                  <li key={idx}>{highlight}</li>
                ))}
              </ul>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

export const useAddOnStyles = makeStyles((theme) => ({
  slider: {
    "& .slick-slider": {
      "width": 156,
      "& .slick-list": {
        width: 156
      }
    }
  },
  container: {
    display: "flex",
    overflowY: "auto",
    overflowX: "hidden",
    maxHeight: "90vh",
    maxWidth: "95vw"
  },
  moreDetails: {
    fontFamily: "Axiforma, serif, sans-serf !important",
    color: "#f7815c",
    fontSize: "12px",
    fontWeight: 600,
    padding: 0,
    textDecoration: "underline",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px"
    }
  },
  img: {
    margin: "auto",
    objectFit: "contain"
  },
  title: {
    fontFamily: "Axiforma, serif, sans-serf !important",
    fontSize: 26,
    fontWeight: 300,
    marginBottom: 20,
    [theme.breakpoints.down("sm")]: {
      textAlign: "center"
    }
  },
  subTitle: {
    fontFamily: "Axiforma, serif, sans-serf !important",
    fontSize: 12,
    marginBottom: 20,
    [theme.breakpoints.down("sm")]: {
      textAlign: "center"
    }
  },
  price: {
    fontFamily: "Axiforma, serif, sans-serf !important",
    fontSize: 20,
    fontWeight: 500,
    marginBottom: 20,
    [theme.breakpoints.down("sm")]: {
      textAlign: "center"
    }
  },
  showQuantity: {
    backgroundColor: "#f3f3f3",
    borderRadius: 20,
    width: 130,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  button: {
    "color": "#f7815c",
    "borderRadius": 20,
    "width": 40,
    "height": 31,
    "border": 0,
    "cursor": "pointer",
    "&:disabled": {
      color: "#d0d0d0"
    }
  },
  btnAdd: {
    "width": "128px",
    "height": "38px",
    "borderRadius": "18px",
    "boxShadow": "none",
    "fontSize": "14px",
    "marginLeft": 20,
    "fontFamily": fontFamily.Axiforma,
    "fontWeight": 400,
    "& .MuiButton-label": { marginTop: "2px" },
    "&:hover": { boxShadow: "none" },
    "&:disabled": {
      background: "#f7815c",
      color: "#fff",
      opacity: 0.5
    }
  },
  add: {
    color: "#f7815c",
    width: 102,
    height: 38,
    marginLeft: 20,
    borderRadius: 20,
    backgroundColor: "white",
    border: "1px solid #f7815c"
  },
  icon: {
    verticalAlign: "sub"
  }
}));
